// Before joining: see yourself, turn the camera and mic on or off, pick which ones, then join.
// (Radix selects portal into this document; the lobby only ever shows in the app tab.)
import { useState } from 'react';
import { AlertTriangle, Mic, MicOff, Video, VideoOff, Volume2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MediaEl, playTestSound, speakerSupported, vTrack } from './media';
import BackgroundPicker from './BackgroundPicker';

function Toggle({ on, onClick, OnIcon, OffIcon, label, tid }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on} data-testid={tid} title={`Turn ${label.toLowerCase()} ${on ? 'off' : 'on'}`}
      className={`flex h-11 items-center gap-2 rounded-full px-5 text-sm font-medium transition-colors duration-150 ${on
        ? 'border border-[#DDE3EA] bg-white text-[#0D1B2A] hover:border-[#4575A8] hover:text-[#4575A8]'
        : 'border border-red-600 bg-red-600 text-white hover:bg-red-700'}`}>
      {on ? <OnIcon className="h-4 w-4" /> : <OffIcon className="h-4 w-4" />}{label}{on ? ' on' : ' off'}
    </button>
  );
}

function DevicePick({ label, list, value, onPick, tid, none }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-[#5C6370]">{label}</span>
      {list.length ? (
        <Select value={value || undefined} onValueChange={onPick}>
          <SelectTrigger className="h-11 border-[#DDE3EA] bg-white shadow-none focus:ring-[#4575A8]/30" data-testid={tid}>
            <SelectValue placeholder={`Choose a ${label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent className="z-[70] bg-white">
            {list.map((d, i) => <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || `${label} ${i + 1}`}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : <p className="text-xs text-muted-foreground" data-testid={`${tid}-none`}>{none}</p>}
    </label>
  );
}

export default function CallLobby({ dc, title, onCancel, guest }) {
  const me = dc.local;
  const v = vTrack(me);
  const level = Math.min(100, Math.round((dc.micLevel || 0) * 400));
  const [testing, setTesting] = useState(false);
  const test = async () => { setTesting(true); try { await playTestSound(dc.devices.speakerId); } catch { /* blocked */ } setTesting(false); };
  return (
    <div className="relative grid gap-8 md:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]" data-testid="mm-lobby">
      <button type="button" onClick={onCancel} aria-label="Close" data-testid="mm-lobby-close"
        className="absolute -right-2 -top-2 rounded-full p-1.5 text-[#8A94A3] transition-colors duration-150 hover:bg-[#F2F5F8] hover:text-[#0D1B2A] sm:-right-4 sm:-top-4">
        <X className="h-5 w-5" />
      </button>
      <div className="space-y-4">
        <div className="relative flex items-center justify-center overflow-hidden rounded-xl bg-[#0D1B2A]" style={{ aspectRatio: '16 / 9' }}>
          {v && me?.video ? <MediaEl track={v} muted mirror className="h-full w-full object-cover" /> : (
            <p className="text-sm text-[#8AA3BC]" data-testid="mm-lobby-camera-off">{me?.video === false ? 'Your camera is off' : 'Starting camera…'}</p>
          )}
          <div className="absolute bottom-3 left-3 right-3 h-1 overflow-hidden rounded-full bg-white/20" title="Mic level">
            <div className="h-full rounded-full bg-[#22C55E] transition-[width] duration-100" style={{ width: `${me?.audio ? level : 0}%` }} data-testid="mm-lobby-mic-level" />
          </div>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <Toggle on={!!me?.audio} onClick={() => dc.setMic(!me?.audio)} OnIcon={Mic} OffIcon={MicOff} label="Mic" tid="mm-lobby-mic" />
          <Toggle on={!!me?.video} onClick={() => dc.setCam(!me?.video)} OnIcon={Video} OffIcon={VideoOff} label="Camera" tid="mm-lobby-cam" />
        </div>
        <div className="rounded-xl border border-[#DDE3EA] p-4">
          <p className="text-sm font-medium text-[#0D1B2A]">Background</p>
          <p className="mb-3 text-xs text-[#5C6370]">Blur it or swap in a picture. Handy from a truck or a job site.</p>
          <BackgroundPicker dc={dc} guest={guest} tid="mm-lobby-bg" />
        </div>
      </div>
      <div className="flex flex-col gap-5 md:pt-1">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#4575A8]">Ready to join?</p>
          <h2 className="mt-1.5 text-2xl font-bold text-[#0D1B2A]" data-testid="mm-lobby-title">{title}</h2>
          <p className="mt-1.5 text-sm text-[#5C6370]">Check your camera and mic. You can change them any time in the call.</p>
        </div>
        <DevicePick label="Camera" list={dc.devices.cameras} value={dc.devices.camId} onPick={(id) => dc.setDevice('camera', id)}
          tid="mm-lobby-camera-select" none="No camera found" />
        <DevicePick label="Microphone" list={dc.devices.mics} value={dc.devices.micId} onPick={(id) => dc.setDevice('mic', id)}
          tid="mm-lobby-mic-select" none="No microphone found" />
        <div className="space-y-1.5">
          {speakerSupported() ? (
            <DevicePick label="Speaker" list={dc.devices.speakers} value={dc.devices.speakerId} onPick={dc.setSpeaker}
              tid="mm-lobby-speaker-select" none="Your computer's default speaker" />
          ) : (
            <p className="text-xs text-[#5C6370]" data-testid="mm-lobby-speaker-default">Sound plays through your computer's default speaker.</p>
          )}
          <button type="button" onClick={test} disabled={testing} data-testid="mm-lobby-test-sound"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4575A8] transition-colors duration-150 hover:text-[#2F5680] disabled:opacity-60">
            <Volume2 className="h-3.5 w-3.5" />{testing ? 'Playing…' : 'Play test sound'}
          </button>
        </div>
        {dc.mediaError && (
          <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="mm-lobby-media-error">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Your camera or mic isn't available ({dc.mediaError}). Allow them from the camera icon in the address bar, or join without them.</span>
          </div>
        )}
        <div className="mt-auto flex items-center gap-3 pt-2">
          <Button className="h-11 flex-1 rounded-full bg-[#4575A8] text-white transition-colors duration-150 hover:bg-[#3A6491]" onClick={dc.join} data-testid="mm-lobby-join">
            Join meeting
          </Button>
          <Button variant="ghost" className="h-11 rounded-full px-5 text-[#5C6370] hover:bg-[#F2F5F8] hover:text-[#0D1B2A]" onClick={onCancel} data-testid="mm-lobby-cancel">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
